import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { Alert, Button, Card, StatusBadge, Tabs, useToast } from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { ApiUser, ApiUserCreate, Role, UserRole } from '@kss/api';
import { userApiService, roleService, userRoleService } from '@kss/api';
import { NameCell } from '../../hooks/useNameMaps';
import { useModulePerms } from '../../hooks/useModulePerms';

// H9 login flow requires a UserRole row for the newly-created user.
// Backend seeds "Administrator" and "Customer" per tenant — resolve
// against those names so the JWT actually carries permissions.
const FALLBACK_ROLE_NAME = 'Administrator';

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
type FormState = {
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirm: string;
  /** H9 multi-role — every RoleId assigned to this user. */
  roleIds: string[];
  /** Read-side compat — primary role name for legacy grids. */
  role: string;
  isActive: boolean;
};
const emptyForm: FormState = {
  userName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirm: '',
  roleIds: [],
  // Placeholder — overwritten on mount by the loaded backend role
  // list (see the roleService.all() effect). Kept non-empty so the
  // Access tab always has *something* selectable if the list load
  // fails before the user opens the form.
  role: FALLBACK_ROLE_NAME,
  isActive: true,
};

const STEPS = ['basic', 'contact', 'access', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info', contact: 'Contact', access: 'Access & status', review: 'Review & save',
};
const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function UserForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const perms = useModulePerms('User');
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Backend-loaded role list. Populates the Access-tab dropdown so we
  // send a *real* tenant-seeded role name (e.g. "Administrator"), not
  // the pre-seed placeholder "staff" that H9 can't resolve.
  const [roles, setRoles] = useState<Role[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await roleService.all();
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setRoles(list);
        // Default the form.role to the first backend role so a CREATE
        // never ships the un-seeded "staff" placeholder. Only set when
        // the user hasn't picked something else yet (form.role still
        // equals the empty-form default).
        if (list.length > 0) {
          setForm((f) =>
            f.role === emptyForm.role && f.roleIds.length === 0
              ? { ...f, role: list[0].name, roleIds: list[0].roleId ? [list[0].roleId] : [] }
              : f,
          );
        }
      } catch {
        /* keep fallback string — CREATE will still send a role field */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setLoadError(null);
        try {
          const rec = await userApiService.get(id);
          if (cancelled) return;
          // H9 multi-role — hydrate the assigned RoleIds from the
          // UserRole junction so the picker shows every role the user
          // is currently in. The `roles[]` read-side (names) is only
          // used for the review summary and legacy compat.
          let assignedRoleIds: string[] = [];
          try {
            const urs = (await userRoleService.filter({ userId: id })) as UserRole[];
            assignedRoleIds = (urs ?? [])
              .filter((u) => u.roleId && u.isActive !== false)
              .map((u) => u.roleId as string);
          } catch {
            // Filter may 400 on a stale schema — fall back to the
            // singular `role` string returned by /User/Get.
            assignedRoleIds = [];
          }
          setForm({
            userName: rec.userName ?? '',
            firstName: rec.firstName ?? '',
            lastName: rec.lastName ?? '',
            email: rec.email ?? '',
            phone: rec.phone ?? '',
            // Never echo the existing password — the input stays blank
            // on edit, and an empty value on PUT is treated as "keep
            // existing" (per the backend contract verified 2026-05-05).
            password: '',
            confirm: '',
            role: rec.role ?? FALLBACK_ROLE_NAME,
            roleIds: assignedRoleIds,
            // Live API uses int `status` (1=Active, 2=Inactive, …) but
            // also still ships a legacy `isActive` boolean alias.
            isActive: rec.isActive ?? rec.status === 1,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg); toastRef.current.error('Load failed', msg);
        } finally { if (!cancelled) setLoading(false); }
      } else setForm(emptyForm);
      setErrors({}); setStep('basic'); setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const validateStep = (s: StepId) => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.userName.trim()) errs.userName = 'Username is required';
      if (!form.firstName.trim()) errs.firstName = 'First name is required';
      if (!form.email.trim()) errs.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    }
    if ((s === 'access' || s === 'review') && !isEdit) {
      // Password required only on CREATE; PUT keeps the existing hash
      // when omitted (backend contract verified 2026-05-05).
      if (!form.password) errs.password = 'Password is required';
      else if (form.password.length < 6) errs.password = 'Password must be at least 6 characters';
      if (form.password !== form.confirm) errs.confirm = 'Passwords do not match';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const goNext = () => {
    const i = STEPS.indexOf(step); if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1]; setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; }); setStep(next);
  };
  const goPrev = () => { const i = STEPS.indexOf(step); if (i === 0) return; setRedTabs((p) => new Set(p).add(step)); setStep(STEPS[i - 1]); };
  const basicValid =
    !!form.userName.trim() &&
    !!form.firstName.trim() &&
    !!form.email.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const displayName = `${form.firstName} ${form.lastName}`.trim() || form.userName;

  const handleSave = async () => {
    // Tab-wise: each tab only validates its own fields; Save & Next
    // on intermediate tabs just advances. API save runs on Review.
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const stepIdx = STEPS.indexOf(step);
    const isLastStep = stepIdx === STEPS.length - 1;
    if (!isLastStep) {
      const next = STEPS[stepIdx + 1];
      setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
      setStep(next);
      return;
    }
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    setSaving(true);
    try {
      // Live API rejects `isActive` on User create/update with
      // `UNKNOWN_FIELD` (verified 2026-05-05) — User uses an int
      // `status` enum instead (1=Active, 2=Inactive). The form's
      // Active toggle controls only the `status` value sent on
      // the wire; `isActive` stays out of the payload.
      if (isEdit && id) {
        // Edit mode: omit `password` so the backend keeps the
        // existing hash. Only post a password when the admin
        // actively types a new one.
        const p: ApiUser = {
          userId: id,
          userName: form.userName,
          firstName: form.firstName,
          lastName: form.lastName || undefined,
          email: form.email,
          phone: form.phone || undefined,
          ...(form.password ? { password: form.password } : {}),
          // H9 multi-role — prefer the explicit `roleIds` array.
          // Backend `UserController.PutAsync` diffs against the
          // live UserRole rows and materialises additions /
          // deletions in the same request. The singular `role`
          // string stays for back-compat with older grids.
          roleIds: form.roleIds,
          role: form.role || FALLBACK_ROLE_NAME,
          status: form.isActive ? 1 : 2,
        };
        await userApiService.update([p]);
        toast.success('Saved', `${displayName} updated`);
      } else {
        // Create mode: backend accepts plaintext `password` and
        // hashes server-side. End-to-end verified — the new user
        // can sign in with that password immediately.
        // Client-stamped UserId avoids Guid.Empty collisions when the
        // backend doesn't stamp its own default. `crypto.randomUUID` is
        // available in every browser we support; guard for the odd
        // insecure-context edge case.
        const newUserId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
        const p: ApiUserCreate & { userId: string } = {
          userId: newUserId,
          userName: form.userName,
          firstName: form.firstName,
          lastName: form.lastName || undefined,
          email: form.email,
          phone: form.phone || undefined,
          password: form.password,
          // H9 multi-role — ship the full RoleIds set. Backend
          // materialises one UserRole row per id in the same
          // request. Fall back to the singular `role` string when
          // the picker was left empty so first-run users don't end
          // up with zero role rows and a permission-less JWT.
          roleIds: form.roleIds,
          role: form.role || FALLBACK_ROLE_NAME,
          status: form.isActive ? 1 : 2,
        };
        // Live `POST /api/User` still returns `data: true` instead of the
        // created entity — recover the id by filtering on `userName`
        // (more reliable than `email` since email isn't always unique
        // across tenants).
        await userApiService.create([p]);
        toast.success('Saved', `${displayName} added`);
      }
      // Review-tab save succeeded — back to the list (matches edit mode).
      navigate('/panel/users');
    } catch (e) { toast.error('Save failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => { const m: Record<string, TabStatus> = {}; redTabs.forEach((s) => (m[s] = 'danger')); return m; }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label} {required && <span className="text-danger">*</span>}</span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Username', 'userName', <input value={form.userName} onChange={(e) => set('userName', e.target.value)} placeholder="jane.doe" className={inputCls} autoComplete="username" />, true)}
      {field('Email', 'email', <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.com" className={inputCls} autoComplete="email" />, true)}
      {field('First name', 'firstName', <input value={form.firstName} onChange={(e) => set('firstName', e.target.value)} placeholder="Jane" className={inputCls} autoComplete="given-name" />, true)}
      {field('Last name', 'lastName', <input value={form.lastName} onChange={(e) => set('lastName', e.target.value)} placeholder="Doe" className={inputCls} autoComplete="family-name" />)}
    </div>
  );
  const contactStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Phone', 'phone', <input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+91..." className={inputCls} />)}
    </div>
  );
  const accessStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field(
        isEdit ? 'New password (optional)' : 'Password',
        'password',
        <input
          type="password"
          value={form.password}
          onChange={(e) => set('password', e.target.value)}
          placeholder={isEdit ? 'Leave blank to keep current' : '6+ characters'}
          className={inputCls}
          autoComplete="new-password"
        />,
        !isEdit,
      )}
      {field(
        'Confirm password',
        'confirm',
        <input
          type="password"
          value={form.confirm}
          onChange={(e) => set('confirm', e.target.value)}
          placeholder={isEdit ? 'Only required when changing' : '6+ characters'}
          className={inputCls}
          autoComplete="new-password"
        />,
        !isEdit,
      )}
      {field('Roles', 'roleIds',
        <div className="rounded-md border border-border bg-surface-card p-2">
          {roles.length === 0 && (
            <div className="p-2 text-xs text-content-muted">
              No roles yet. Create one under <code className="rounded bg-surface-card-hover px-1">/panel/roles</code>.
            </div>
          )}
          <div className="max-h-40 overflow-y-auto">
            {roles.map((r) => {
              const rid = r.roleId ?? '';
              const on = !!rid && form.roleIds.includes(rid);
              return (
                <label key={rid || r.name} className="flex cursor-pointer items-center gap-2 py-1 text-sm hover:bg-surface-card-hover">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!rid}
                    onChange={() => {
                      if (!rid) return;
                      const next = new Set(form.roleIds);
                      if (next.has(rid)) next.delete(rid);
                      else next.add(rid);
                      const nextIds = Array.from(next);
                      // Keep the singular `role` string in sync with
                      // the first picked role so the review summary
                      // and back-compat grids stay meaningful.
                      const firstRoleName =
                        (nextIds.length > 0 && roles.find((rr) => rr.roleId === nextIds[0])?.name) || form.role;
                      setForm((f) => ({ ...f, roleIds: nextIds, role: firstRoleName }));
                    }}
                    className="h-4 w-4 cursor-pointer rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-content-primary">{r.name}</span>
                  {r.isSystem && <span className="text-[10px] uppercase text-content-muted">system</span>}
                </label>
              );
            })}
          </div>
        </div>,
      )}
      {field('Status', 'isActive',
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>)}
    </div>
  );
  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success"><CheckCircle2 className="h-4 w-4" /><span className="text-sm font-medium">Ready to save</span></div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Username" value={form.userName} />
          <Row label="Email" value={form.email} />
          <Row label="Name" value={`${form.firstName} ${form.lastName}`.trim()} />
          <Row label="Phone" value={form.phone} />
          {/* Multi-role summary — every RoleId picked in the Access
              tab. Falls back to the singular `role` string when the
              picker is empty (older payload / staff bootstrap). */}
          <Row
            label="Roles"
            value={
              form.roleIds.length > 0
                ? form.roleIds
                    .map((rid) => roles.find((rr) => rr.roleId === rid)?.name ?? rid)
                    .join(', ')
                : <NameCell kind="role" id={form.role} />
            }
          />
          <Row label="Password" value={form.password ? '••••••' : isEdit ? '(unchanged)' : ''} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'contact' as const, label: STEP_LABELS.contact, content: contactStep },
    { id: 'access' as const, label: STEP_LABELS.access, content: accessStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs tabs={tabs} activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus} barOnly />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/users')}>Cancel</Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!basicValid || saving || (isLast && !canSave)}
              title={isLast && !canSave ? `You don't have permission to ${isEdit ? 'update' : 'create'} users` : undefined}
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value !== '' && value != null ? value : <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
